import styled from 'styled-components';

import NotificationStyled from '../../../../../components/Notification';
import StickyNav from '../../../../../components/StickyNav';
import { IconInfoCircle as InfoCircle } from '../../../../../core';
import UseCasePalettes from '../../../components/UseCasePalettes';

interface Props {
  className?: string;
}

const UseCaseTab = ({ className = '' }: Props) => (
  <div className={className}>
    <div className="tab-content">
      <div className="definition">
        <h2 className="title" id="definition">
          Definition
        </h2>
        <p>
          A scatter map is a type of data visualization that represents individual data points as
          markers on a geographic map. Each marker&apos;s position corresponds to a specific
          geographic location, and additional attributes such as size, color, or shape can encode
          further information about the data. Scatter maps are ideal for analyzing spatial
          distributions, identifying clusters or patterns, and visualizing relationships between
          geographic and non-geographic variables.
        </p>
      </div>
      <div className="usage">
        <h2 className="title" id="usage">
          Usage
        </h2>
        <p className="subtitle">
          Map-Scatter charts are a powerful tool for visualizing geographic data, offering insights
          into relationships between variables at specific locations:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Analyzing Correlations Between Variables:</strong> Map-scatter charts plot data
            points on a map, making it easy to identify correlations between two variables, such as
            income and education levels across different regions.
          </li>
          <li>
            <strong>Highlighting Geographic Distributions:</strong> These charts are perfect for
            displaying how a particular variable, such as temperature or sales, is distributed
            geographically, revealing concentration patterns across locations.
          </li>
          <li>
            <strong>Uncovering Clusters and Outliers:</strong> By plotting data on a geographic map,
            map-scatter charts help identify clusters of high or low values and pinpoint outliers
            that may require further analysis or action.
          </li>
        </ul>
      </div>
      <div className="limitations">
        <h2 className="title" id="limitations">
          Limitations
        </h2>
        <p className="subtitle">
          Map-Scatter charts are a valuable tool for visualizing spatial data, but they come with
          certain limitations that should be considered:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Overlapping Points in Dense Areas:</strong> When there are many data points in a
            specific region, they can overlap, making it difficult to distinguish individual data
            points and potentially obscuring important patterns or trends.
          </li>
          <li>
            <strong>Lack of Context for Data Points:</strong> Map-Scatter charts show data points
            geographically, but without proper labels or additional context, it can be challenging
            to understand the significance of each point, especially when mapping large datasets.
          </li>
          <li>
            <strong>Limited Scalability for Large Datasets:</strong> As the number of data points
            increases, Map-Scatter charts can become cluttered and harder to read, reducing their
            effectiveness in providing clear insights for larger datasets.
          </li>
        </ul>
      </div>
      <div className="colors">
        <h2 className="title" id="colors">
          Colors
        </h2>
        <p>
          Color selection in Map-Scatter charts is essential for enhancing the clarity and impact of
          the visual representation. Typically, a sequential color scale is used, where lighter
          colors represent lower values and darker shades indicate higher values, helping to
          differentiate between varying levels of data. When there is a central reference point, a
          diverging color palette can highlight both positive and negative deviations, providing a
          more comprehensive understanding of the data&apos;s distribution. Proper use of color
          helps in emphasizing patterns and trends, making the chart more intuitive and engaging.
        </p>
        <NotificationStyled
          icon={<InfoCircle />}
          title="For in-depth information on this topic, please refer to the Colors page."
          color="blue"
          className="info-notification"
        />
        <UseCasePalettes palettes={['qualitative', 'sequential', 'diverging']} />
      </div>

      <div className="challenges">
        <h2 className="title">Challenges</h2>
        <p>
          Map-Scatter charts effectively display data distributions geographically, but they come
          with their own set of challenges:
        </p>
        <ul className="ordered-list-badge-num">
          <li>
            <strong>Population Bias:</strong> Points tend to gather where people live, so a scatter
            map can show population density rather than the variable being studied. Comparing
            against a baseline or normalizing the data keeps the pattern meaningful.
          </li>
          <li>
            <strong>Too Many Encodings on One Marker:</strong> Size, color and shape can all carry
            data, but used together on the same markers they compete for attention, and none of them
            reads clearly. One or two encodings per map keep the markers legible.
          </li>
          <li>
            <strong>Location Precision and Privacy:</strong> Exact coordinates can carry geocoding
            errors or reveal where individuals live. Aggregating or slightly offsetting points
            protects privacy, at the cost of some positional accuracy.
          </li>
        </ul>
      </div>
    </div>
    <div>
      <StickyNav title="Contents" />
    </div>
  </div>
);

const StyledUseCaseTab = styled(UseCaseTab)`
  display: flex;
  gap: 2.5rem;
  justify-content: space-between;

  > .tab-content {
    width: 100%;
    .usage,
    .definition,
    .limitations,
    .challenges {
      max-width: 700px;
    }
    > .colors {
      > .info-notification {
        margin-top: 24px;
        margin-bottom: 40px;
      }

      > .palette {
        > .palette-title {
          color: ${({ theme }) => theme.neutralText};
          font-size: ${({ theme }) => theme.fontSizes.base};
          line-height: ${({ theme }) => theme.lineHeights.base};
          margin-bottom: 4px;
        }
      }

      .color-palette {
        margin-top: 12px;
      }
    }

    > .challenges {
      > .ordered-list-badge-num {
        margin-top: 16px;
      }
    }

    .title {
      color: ${({ theme }) => theme.neutralText};
      font-weight: 600;
      font-size: ${({ theme }) => theme.fontSizes.lg};
      line-height: ${({ theme }) => theme.lineHeights.lg};
      margin-bottom: 8px;
      margin-top: 40px;
    }

    p {
      color: ${({ theme }) => theme.neutralTextWeak};
      font-size: ${({ theme }) => theme.fontSizes.sm};
      line-height: ${({ theme }) => theme.lineHeights.sm};
      font-weight: 400;
      max-width: 700px;
    }

    .subtitle {
      margin-bottom: 16px;
    }

    // styles for list with badge
    .ordered-list-badge-num {
      font-size: 0.875rem;
      line-height: 1.4285714286;
      margin-left: 29px;
      counter-reset: num;
      list-style: none;
      color: ${({ theme }) => theme.neutralTextWeak};

      li {
        position: relative;
        margin-bottom: 16px;

        &::before {
          content: counter(num);
          counter-increment: num;
          position: absolute;
          left: -1.75rem;
          top: 0;
          width: 1.25rem;
          height: 1.25rem;
          border-radius: 0.1875rem;
          text-align: center;
          font-size: 0.6875rem;
          line-height: 1.8;
          color: ${({ theme }) => theme.primaryText};
          background-color: ${({ theme }) => theme.primaryBackgroundActive};
        }
      }
    }
  }
`;

export default StyledUseCaseTab;
